/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Typography,
  MenuItem,
  Menu,
  Breadcrumbs,
  TextField,
  Divider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { workflowActions, deleteWorkflow } from '../../reducers/workflow';
import { Link, useNavigate } from 'react-router-dom';
import { useNavigationListener } from '../components/hooks/useNavigationListener';
import { WorkflowTable } from '../components/workflow/WorkflowTable';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PaperCard } from '../components/Common';
import { RootState } from '@ink-ai/portal/reducers';

export const WorkflowList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const workflow = useSelector((state: RootState) => state.workflow);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkflows, setSelectedWorkflows] = useState<string[]>([]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    dispatch(workflowActions.setLoading(true));
    handleMenuClose();
    dispatch(deleteWorkflow(selectedWorkflows));
    setSelectedWorkflows([]);
  };

  const handleUpdate = () => {
    if (selectedWorkflows.length === 1) {
      const selectedWorkflow = workflow.workflows.find(
        (w) => w.workflowId === selectedWorkflows[0],
      );
      if (selectedWorkflow) {
        navigate(`/workflows/${selectedWorkflow.workflowId}/update`);
      }
    }
    handleMenuClose();
  };

  useNavigationListener();

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t('workflows.navigation.home')}</Link>
        <Typography color="textPrimary">
          {t('workflows.navigation.workflows')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left', mb: 2 }}>
          {t('workflows.title')}
        </Typography>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <TextField
            label={t('workflows.search.label')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <Box>
            <Button
              ref={actionsButtonRef}
              color="primary"
              onClick={handleMenuOpen}
              endIcon={<ArrowDropDownIcon />}
              variant="outlined"
              sx={{
                borderColor: 'black',
                color: 'black',
                '&:hover': {
                  borderColor: 'black',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              disabled={selectedWorkflows.length === 0}
            >
              {t('workflows.actions.button')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ ml: 1 }}
              onClick={() => navigate('/workflows/import')}
            >
              {t('workflows.actions.import')}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              slotProps={{
                paper: {
                  style: {
                    minWidth: actionsButtonRef.current?.offsetWidth,
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleUpdate}
                disabled={selectedWorkflows.length !== 1}
              >
                {t('workflows.dialog.buttons.update')}
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={handleDelete}
                disabled={selectedWorkflows.length === 0}
              >
                {t('workflows.actions.delete')}
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        <WorkflowTable
          selectedWorkflows={selectedWorkflows}
          onSelectedWorkflowsChange={setSelectedWorkflows}
          searchTerm={searchTerm}
          page={page}
          onPageChange={setPage}
        />
      </PaperCard>
    </Container>
  );
};
